// src/pages/loadletter/StaffDashboard.tsx

import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  FileText,
  Download,
  CheckCircle,
  HelpCircle,
  Clock,
  BarChart,
  Calendar,
  AlertTriangle,
  Send,
  Eye,
  MessageSquare,
  User,
  ClipboardList,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useOpenIntentionPeriod } from '../../contexts/OpenIntentionPeriodContext';
import {
  getLoadLettersByStaffId,
  getLoadLetterHistory,
  acknowledgeLoadLetter,
  createQuery,
  getQueriesByLetterId,
} from '../../services/loadLetterService';
import { LoadLetter, LoadLetterQuery } from '../../types/loadLetter';
import { generateLoadLetterPdf } from '../../utils/loadLetterPdfGenerator';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';

const StaffDashboard: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const { staff } = useData();
  const { openPeriod, isOpen } = useOpenIntentionPeriod();

  // States
  const [loadLetters, setLoadLetters] = useState<LoadLetter[]>([]);
  const [priorVersions, setPriorVersions] = useState<LoadLetter[]>([]);
  const [queries, setQueries] = useState<LoadLetterQuery[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<LoadLetter | null>(null);
  const [showLetterPreview, setShowLetterPreview] = useState<boolean>(false);
  const [showQueryForm, setShowQueryForm] = useState<boolean>(false);
  const [isAcknowledging, setIsAcknowledging] = useState<boolean>(false);
  const [isSubmittingQuery, setIsSubmittingQuery] = useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [newQuery, setNewQuery] = useState({
    subject: '',
    description: '',
  });

  // Removed expandedSections state - no longer needed without previous letters section

  // Get current staff member
  const currentStaff = user?.email
    ? staff.find(s => s.email === user.email)
    : null;

  // Fetch load letters when component mounts
  useEffect(() => {
    if (currentStaff) {
      fetchLoadLetters();
    }
  }, [currentStaff]);

  const fetchLoadLetters = async () => {
    if (!currentStaff) return;

    try {
      setLoading(true);
      const letters = await getLoadLettersByStaffId(currentStaff.id);
      setLoadLetters(letters);

      // Fetch prior (superseded) versions for each current letter so staff can see
      // the exact content they previously received or acknowledged.
      const priors: LoadLetter[] = [];
      for (const letter of letters) {
        const history = await getLoadLetterHistory(
          currentStaff.id,
          letter.academicYear,
          letter.semester
        );
        priors.push(...history.filter(h => h.id !== letter.id));
      }
      setPriorVersions(priors);

      // Get queries for all letters
      const allQueries: LoadLetterQuery[] = [];
      for (const letter of letters) {
        const letterQueries = await getQueriesByLetterId(letter.id);
        allQueries.push(...letterQueries);
      }
      setQueries(allQueries);
    } catch (error) {
      console.error('Error fetching load letters:', error);
      addToast('Failed to load your teaching load letters', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get current letter (most recent released/acknowledged)
  // Note: Only showing current letter for v1.0.0 launch - no previous letters section
  const currentLetter = loadLetters.find(
    l => l.status === 'released' || l.status === 'acknowledged' || l.status === 'queried'
  );

  // Handle letter acknowledgment
  const handleAcknowledge = async (letterId: string) => {
    setIsAcknowledging(true);
    try {
      await acknowledgeLoadLetter(letterId);
      addToast('Load letter acknowledged successfully', 'success');
      await fetchLoadLetters(); // Refresh data
    } catch (error) {
      console.error('Error acknowledging letter:', error);
      addToast('Failed to acknowledge load letter', 'error');
    } finally {
      setIsAcknowledging(false);
    }
  };

  // Handle query submission
  const handleSubmitQuery = async () => {
    if (!currentLetter || !currentStaff) return;

    if (!newQuery.subject.trim() || !newQuery.description.trim()) {
      addToast('Please fill in all fields', 'error');
      return;
    }

    setIsSubmittingQuery(true);
    try {
      await createQuery({
        loadLetterId: currentLetter.id,
        staffId: currentStaff.id,
        staffName: currentStaff.name,
        subject: newQuery.subject,
        description: newQuery.description,
        submittedDate: new Date().toISOString(),
        status: 'pending',
      });

      addToast('Query submitted successfully', 'success');
      setNewQuery({ subject: '', description: '' });
      setShowQueryForm(false);
      await fetchLoadLetters(); // Refresh data
    } catch (error) {
      console.error('Error submitting query:', error);
      addToast('Failed to submit query', 'error');
    } finally {
      setIsSubmittingQuery(false);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async (letter: LoadLetter) => {
    setIsDownloadingPdf(true);
    try {
      await generateLoadLetterPdf(letter);
      addToast('PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      addToast('Failed to download PDF. Please try again.', 'error');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'released':
        return (
          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            <CheckCircle className="h-3 w-3 mr-1" />
            Released
          </span>
        );
      case 'acknowledged':
        return (
          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            <CheckCircle className="h-3 w-3 mr-1" />
            Acknowledged
          </span>
        );
      case 'queried':
        return (
          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            <HelpCircle className="h-3 w-3 mr-1" />
            Query Submitted
          </span>
        );
      default:
        return null;
    }
  };

  // Get utilization color
  const getUtilizationColor = (utilization: number) => {
    if (utilization < 85) return 'text-navy-600';
    if (utilization < 95) return 'text-navy-500';
    if (utilization <= 105) return 'text-ok-fg';
    if (utilization <= 115) return 'text-warn-fg';
    return 'text-danger-fg';
  };

  // Render letter preview modal
  const renderLetterPreview = () => {
    if (!selectedLetter) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="p-6 border-b flex justify-between items-center">
            <h3 className="text-section text-ink-900">
              Load Letter: {selectedLetter.semester} {selectedLetter.academicYear}
              {selectedLetter.supersededAt && (
                <span className="ml-2 text-sm font-normal text-ink-500">
                  (v{selectedLetter.version ?? 1} · superseded {new Date(selectedLetter.supersededAt).toLocaleDateString()})
                </span>
              )}
            </h3>
            <button
              className="text-ink-400 hover:text-ink-500 text-2xl"
              onClick={() => {
                setShowLetterPreview(false);
                setSelectedLetter(null);
              }}
            >
              &times;
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div
              className="prose max-w-none print-letter"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLetter.generatedHtml || '<p>No content available</p>') }}
            />
          </div>

          <div className="p-6 border-t flex justify-between no-print">
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={() => handleDownloadPdf(selectedLetter)}
                loading={isDownloadingPdf}
                disabled={isDownloadingPdf}
              >
                Download PDF
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLetterPreview(false);
                  setSelectedLetter(null);
                }}
              >
                Close
              </Button>
              {/* Acknowledge button in modal hidden for this release
              {selectedLetter.status === 'released' && (
                <Button
                  variant="primary"
                  leftIcon={<CheckCircle className="h-4 w-4" />}
                  onClick={() => {
                    handleAcknowledge(selectedLetter.id);
                    setShowLetterPreview(false);
                    setSelectedLetter(null);
                  }}
                  loading={isAcknowledging}
                >
                  Acknowledge
                </Button>
              )}
              */}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render query form modal
  const renderQueryForm = () => {
    if (!showQueryForm) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="p-6 border-b">
            <h3 className="text-section text-ink-900">Submit a Query or Concern</h3>
            <p className="text-sm text-ink-500 mt-1">
              If you have questions or concerns about your teaching load, please submit them here.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Subject *
              </label>
              <input
                type="text"
                className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                placeholder="Brief description of your query"
                value={newQuery.subject}
                onChange={e => setNewQuery({ ...newQuery, subject: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Description *
              </label>
              <textarea
                className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                rows={6}
                placeholder="Provide details about your query or concern..."
                value={newQuery.description}
                onChange={e => setNewQuery({ ...newQuery, description: e.target.value })}
              />
            </div>

            <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
              <p className="text-sm text-navy-700">
                Your query will be reviewed by the timetabling team, and you'll receive a response
                within 3-5 business days.
              </p>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowQueryForm(false);
                setNewQuery({ subject: '', description: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={<Send className="h-4 w-4" />}
              onClick={handleSubmitQuery}
              loading={isSubmittingQuery}
            >
              Submit Query
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const intentionsBanner = isOpen ? (
    <Card>
      <div className="p-4 sm:p-6 bg-navy-50 border border-navy-200 rounded-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <ClipboardList className="h-6 w-6 text-navy-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-navy-900">
              Teaching intentions are open
            </h3>
            <p className="text-sm text-navy-800">
              Please submit your teaching intentions for next year
              {openPeriod?.dueDate ? ` by ${openPeriod.dueDate.toLocaleDateString()}` : ''}
              .
            </p>
          </div>
        </div>
        <Link
          to="/intentions/submit"
          className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium bg-navy-600 text-white hover:bg-navy-700 transition-colors whitespace-nowrap"
        >
          Submit now
        </Link>
      </div>
    </Card>
  ) : null;

  // Loading state
  if (loading) {
    return (
      <div>
        <PageHeader
          title="My Teaching Load"
          description="View your teaching load letters and manage acknowledgments"
        />
        <Card>
          <div className="flex justify-center py-12">
            <Spinner size="lg" label="Loading your teaching load information…" />
          </div>
        </Card>
      </div>
    );
  }

  // No staff member found
  if (!currentStaff) {
    return (
      <div>
        <PageHeader
          title="My Teaching Load"
          description="View your teaching load letters and manage acknowledgments"
        />
        <Card>
          <div className="text-center py-12">
            <User className="h-12 w-12 text-ink-400 mx-auto mb-4" />
            <p className="text-ink-500">Unable to find your staff profile.</p>
            <p className="text-sm text-ink-400 mt-2">Please contact the timetabling team.</p>
          </div>
        </Card>
      </div>
    );
  }

  // No load letters
  if (loadLetters.length === 0) {
    return (
      <div>
        <PageHeader
          title="My Teaching Load"
          description="View your teaching load letters and manage acknowledgments"
        />
        {intentionsBanner}
        <Card>
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title="No teaching load letters available yet."
            description="Your load letter will appear here once it's released by the timetabling team."
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="My Teaching Load"
        description={`Welcome ${currentStaff.name}`}
      />

      {intentionsBanner}

      {/* Current Load Letter */}
      {currentLetter && (
        <Card className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-card-title text-ink-900 flex items-center">
                Current Load Letter: {currentLetter.semester} {currentLetter.academicYear}
                <span className="ml-3">{getStatusBadge(currentLetter.status)}</span>
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                Issued: {new Date(currentLetter.issueDate).toLocaleDateString()} |
                Due: {new Date(currentLetter.dueDate).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="primary"
              leftIcon={<Eye className="h-5 w-5" />}
              onClick={() => {
                setSelectedLetter(currentLetter);
                setShowLetterPreview(true);
              }}
              className="flex-1 py-3 text-base"
            >
              View Full Letter
            </Button>

            {/* Acknowledge Letter button hidden for this release
            {currentLetter.status === 'released' && (
              <Button
                variant="primary"
                leftIcon={<CheckCircle className="h-5 w-5" />}
                onClick={() => handleAcknowledge(currentLetter.id)}
                loading={isAcknowledging}
                className="flex-1 py-3 text-base"
              >
                Acknowledge Letter
              </Button>
            )}
            */}

            <Button
              variant="secondary"
              leftIcon={<Download className="h-5 w-5" />}
              onClick={() => handleDownloadPdf(currentLetter)}
              loading={isDownloadingPdf}
              disabled={isDownloadingPdf}
              className="flex-1 py-3 text-base"
            >
              Download PDF
            </Button>
          </div>
        </Card>
      )}

      {/* Prior versions — superseded letters the staff member previously received/acknowledged */}
      {priorVersions.length > 0 && (
        <Card className="mb-6">
          <h3 className="text-card-title text-ink-900 mb-1 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-ink-500" />
            Previous Versions
          </h3>
          <p className="text-sm text-ink-500 mb-4">
            Earlier versions of your load letter. These were replaced when your load changed.
            The content you see below is exactly what you received at the time.
          </p>
          <div className="divide-y divide-ink-200">
            {priorVersions.map(letter => (
              <div key={letter.id} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-ink-900">
                    v{letter.version ?? 1} · {letter.semester} {letter.academicYear}
                    <span className="ml-3">{getStatusBadge(letter.status)}</span>
                  </p>
                  <p className="text-xs text-ink-500 mt-1">
                    Issued {new Date(letter.issueDate).toLocaleDateString()}
                    {letter.acknowledgedDate && ` · Acknowledged ${new Date(letter.acknowledgedDate).toLocaleDateString()}`}
                    {letter.supersededAt && ` · Replaced ${new Date(letter.supersededAt).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Eye className="h-4 w-4" />}
                    onClick={() => {
                      setSelectedLetter(letter);
                      setShowLetterPreview(true);
                    }}
                  >
                    View
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={() => handleDownloadPdf(letter)}
                    disabled={isDownloadingPdf}
                  >
                    PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Queries */}
      {queries.length > 0 && (
        <Card>
          <h3 className="text-card-title text-ink-900 mb-4">My Queries</h3>
          <div className="space-y-4">
            {queries.map(query => (
              <div key={query.id} className="border rounded-md p-4">
                <div className="flex justify-between items-start mb-2">
                  <p className="font-medium text-ink-900">{query.subject}</p>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      query.status === 'resolved'
                        ? 'bg-ok-bg text-ok-fg'
                        : query.status === 'in_progress'
                        ? 'bg-navy-100 text-navy-800'
                        : 'bg-ink-100 text-ink-900'
                    }`}
                  >
                    {query.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-ink-500 mb-2">{query.description}</p>
                <p className="text-xs text-ink-500">
                  Submitted: {new Date(query.submittedDate).toLocaleDateString()}
                </p>
                {query.response && (
                  <div className="mt-3 bg-navy-50 border border-navy-200 rounded-md p-3">
                    <p className="text-xs font-medium text-navy-900 mb-1">Response:</p>
                    <p className="text-sm text-navy-800">{query.response}</p>
                    {query.responseDate && (
                      <p className="text-xs text-navy-600 mt-1">
                        Responded: {new Date(query.responseDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Modals */}
      {showLetterPreview && renderLetterPreview()}
      {showQueryForm && renderQueryForm()}
    </div>
  );
};

export default StaffDashboard;
